import { useEffect, useRef, useState } from 'react';
import { Mic } from 'lucide-react';
import { createRecorder, type RecorderHandle } from '../lib/recorder';
import { formatDuration } from '../lib/format';
import { cn } from '../lib/utils';

interface PttButtonProps {
  stream: MediaStream;
  disabled?: boolean;
  /** Reason the button is disabled (used for accessibility hints). */
  disabledReason?: string;
  minDurationMs?: number;
  recorderMimeType?: string;
  /** Called once recording starts. */
  onStart?: () => void;
  /**
   * Called once the recorder has stopped and produced a blob. The promise
   * lets the host show an "uploading…" state until the network round-trip
   * resolves.
   */
  onStop: (blob: Blob) => Promise<void>;
}

type PttPhase = 'idle' | 'recording' | 'uploading' | 'too-short' | 'error';

const SPACE_KEY = ' ';

export default function PttButton({
  stream,
  disabled = false,
  disabledReason,
  minDurationMs = 400,
  recorderMimeType,
  onStart,
  onStop,
}: PttButtonProps) {
  const recorderRef = useRef<RecorderHandle | null>(null);
  const rafRef = useRef<number | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [phase, setPhase] = useState<PttPhase>('idle');
  const [level, setLevel] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    const handle = createRecorder(stream, recorderMimeType ? { mimeType: recorderMimeType } : {});
    recorderRef.current = handle;
    return () => {
      handle.dispose();
      recorderRef.current = null;
    };
  }, [stream, recorderMimeType]);

  function tick() {
    const rec = recorderRef.current;
    if (!rec) return;
    setLevel(rec.readLevel());
    setElapsed(rec.elapsedMs());
    if (rec.isRecording()) {
      rafRef.current = window.requestAnimationFrame(tick);
    }
  }

  function startRecording() {
    if (disabled || phase !== 'idle') return;
    const rec = recorderRef.current;
    if (!rec) return;
    setErrMsg(null);
    setPhase('recording');
    setLevel(0);
    setElapsed(0);
    rec.start();
    onStart?.();
    rafRef.current = window.requestAnimationFrame(tick);
  }

  async function stopRecording() {
    const rec = recorderRef.current;
    if (!rec || phase !== 'recording') return;
    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const ms = rec.elapsedMs();
    const blob = await rec.stop();
    setLevel(0);
    if (ms < minDurationMs) {
      setPhase('too-short');
      window.setTimeout(() => {
        setPhase((p) => (p === 'too-short' ? 'idle' : p));
      }, 1400);
      return;
    }
    setPhase('uploading');
    try {
      await onStop(blob);
      setPhase('idle');
      setElapsed(0);
    } catch (e) {
      setPhase('error');
      setErrMsg(e instanceof Error ? e.message : 'Could not upload your answer.');
    }
  }

  function handlePointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    e.preventDefault();
    buttonRef.current?.setPointerCapture?.(e.pointerId);
    startRecording();
  }
  function handlePointerUp(e: React.PointerEvent<HTMLButtonElement>) {
    e.preventDefault();
    try {
      buttonRef.current?.releasePointerCapture?.(e.pointerId);
    } catch {
      // pointer was already released
    }
    void stopRecording();
  }
  function handlePointerCancel(e: React.PointerEvent<HTMLButtonElement>) {
    e.preventDefault();
    void stopRecording();
  }

  useEffect(() => {
    let spaceDown = false;
    function isFromInteractiveElement(target: EventTarget | null) {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      return (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        target.isContentEditable
      );
    }
    function down(ev: KeyboardEvent) {
      if (ev.key !== SPACE_KEY) return;
      if (ev.repeat || spaceDown) return;
      if (isFromInteractiveElement(ev.target)) return;
      ev.preventDefault();
      spaceDown = true;
      startRecording();
    }
    function up(ev: KeyboardEvent) {
      if (ev.key !== SPACE_KEY) return;
      if (!spaceDown) return;
      spaceDown = false;
      ev.preventDefault();
      void stopRecording();
    }
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, disabled]);

  const isBusy = phase === 'recording' || phase === 'uploading';

  let label = 'Press and hold to answer';
  if (disabled) label = disabledReason ?? 'Wait…';
  else if (phase === 'recording') label = 'Recording — release when done';
  else if (phase === 'uploading') label = 'Sending…';
  else if (phase === 'too-short') label = 'Hold a bit longer';
  else if (phase === 'error') label = 'Try again';

  const buttonColor = cn(
    'relative inline-flex h-[168px] w-[168px] select-none flex-col items-center justify-center rounded-full border-[3px] border-transparent font-semibold text-sm transition-[background-color,transform,box-shadow] duration-150 ease-out',
    'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/40',
    !disabled && phase === 'idle' && 'bg-primary text-primary-foreground shadow-[0_4px_18px_hsl(var(--primary)/0.35)] hover:bg-primary/90 active:scale-[0.97]',
    phase === 'recording' && 'bg-destructive text-destructive-foreground animate-ptt-pulse',
    phase === 'uploading' && 'bg-muted-foreground text-primary-foreground cursor-progress',
    (phase === 'too-short' || phase === 'error') && 'bg-destructive text-destructive-foreground',
    disabled && 'bg-muted text-muted-foreground shadow-none cursor-not-allowed',
  );

  return (
    <div className="flex flex-col items-center gap-3 pb-2 pt-6">
      <button
        ref={buttonRef}
        type="button"
        className={cn(buttonColor, 'max-sm:h-[148px] max-sm:w-[148px]')}
        disabled={disabled || phase === 'uploading'}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onPointerLeave={(e) => {
          if (phase === 'recording') handlePointerUp(e);
        }}
        onContextMenu={(e) => e.preventDefault()}
        aria-pressed={phase === 'recording'}
        aria-label={label}
      >
        <Mic className="mb-1.5 h-10 w-10" strokeWidth={2} />
        <span className="block max-w-[9rem] text-center leading-tight">{label}</span>
        {phase === 'recording' && (
          <span
            className="absolute bottom-4 font-mono text-xs tabular-nums opacity-85"
            aria-live="polite"
          >
            {formatDuration(elapsed)}
          </span>
        )}
      </button>

      <div
        className="h-1.5 w-56 overflow-hidden rounded-full bg-muted"
        aria-hidden="true"
      >
        <div
          className="h-full bg-primary transition-[width,opacity] duration-100 ease-out"
          style={{
            width: `${Math.round(level * 100)}%`,
            opacity: phase === 'recording' ? 1 : 0.2,
          }}
        />
      </div>

      <p className="m-0 text-center text-xs text-muted-foreground">
        {isBusy
          ? phase === 'uploading'
            ? 'Saving your answer…'
            : 'Speak clearly and release when you are done.'
          : 'Tip: you can also hold the SPACE bar instead of the button.'}
      </p>

      {phase === 'too-short' && (
        <p
          className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="status"
        >
          That was very short — please hold the button while you answer.
        </p>
      )}
      {phase === 'error' && errMsg && (
        <p
          className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          {errMsg}
        </p>
      )}
    </div>
  );
}
